/**
 * Challenges on this device: the challenges it created
 * (for Today's "Yesterday's challenge" line), the challenge a game is being played
 * from (per tab, in sessionStorage), and creating a challenge once the solve has
 * reached the api (asked for ahead of the tap, recorded when it's shared). Everything is
 * versioned and parsed defensively; nothing throws.
 */
import { addDays, type DateKey, isLevel, type Level } from '@arrow/game-engine';

import { createChallenge, type CreateChallengeResult } from '../api/client.ts';
import type { ChallengeContext } from '../game/challenge.ts';
import { type KeyValueStore, memoryStore } from '../storage/kv.ts';
import { track } from './analytics.ts';
import { platformStore } from './config.ts';
import { getDeviceId } from './identity.ts';
import { pushSolveFor } from './sync.ts';

export const challengeKeys = {
	mine: 'arrow:v1:challenges:mine',
	dismissed: 'arrow:v1:challenges:dismissed',
	/** sessionStorage: the challenge a game slot is being played from. */
	playing: (slot: string) => `arrow:v1:challenge:${slot}`,
} as const;

// --- Challenges this device created ------------------------------------------------------

export interface MyChallenge {
	code: string;
	/** The local day it was (last) shared. */
	createdOn: DateKey;
	number: number;
	level: Level;
	/** The creator's time (what friends are racing). */
	durationMs: number;
}

const MAX_MINE = 30;

function isMine(v: unknown): v is MyChallenge {
	if (typeof v !== 'object' || v === null) return false;
	const c = v as Record<string, unknown>;
	return (
		typeof c.code === 'string' &&
		typeof c.createdOn === 'string' &&
		/^\d{4}-\d{2}-\d{2}$/.test(c.createdOn) &&
		typeof c.number === 'number' &&
		isLevel(c.level) &&
		typeof c.durationMs === 'number' &&
		Number.isFinite(c.durationMs)
	);
}

export function myChallenges(store: KeyValueStore = platformStore): MyChallenge[] {
	try {
		const parsed = JSON.parse(store.get(challengeKeys.mine) ?? '{}') as unknown;
		const list = (parsed as { v?: unknown; items?: unknown }).items;
		return (parsed as { v?: unknown }).v === 1 && Array.isArray(list) ? list.filter(isMine) : [];
	} catch {
		return [];
	}
}

export function rememberChallenge(c: MyChallenge, store: KeyValueStore = platformStore): void {
	const rest = myChallenges(store).filter((m) => m.code !== c.code);
	store.set(challengeKeys.mine, JSON.stringify({ v: 1, items: [...rest, c].slice(-MAX_MINE) }));
}

function dismissedCodes(store: KeyValueStore): string[] {
	try {
		const parsed = JSON.parse(store.get(challengeKeys.dismissed) ?? '[]') as unknown;
		return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
	} catch {
		return [];
	}
}

export function dismissFeedback(code: string, store: KeyValueStore = platformStore): void {
	store.set(challengeKeys.dismissed, JSON.stringify([...dismissedCodes(store).filter((c) => c !== code), code].slice(-MAX_MINE)));
}

/**
 * The challenge whose results Today should report: the latest one this device shared
 * today or yesterday (local days) that hasn't been dismissed.
 */
export function feedbackChallenge(
	todayKey: DateKey,
	store: KeyValueStore = platformStore,
): { challenge: MyChallenge; when: 'today' | 'yesterday' } | null {
	const yesterday = addDays(todayKey, -1);
	const dismissed = new Set(dismissedCodes(store));
	const mine = myChallenges(store).filter((c) => !dismissed.has(c.code) && (c.createdOn === todayKey || c.createdOn === yesterday));
	// Yesterday's first: today's has hardly had time.
	const pick = mine.filter((c) => c.createdOn === yesterday).pop() ?? mine.pop();
	return pick ? { challenge: pick, when: pick.createdOn === todayKey ? 'today' : 'yesterday' } : null;
}

// --- The challenge a game is played from (this tab) ----------------------------------------

function sessionStore(): KeyValueStore {
	try {
		if (typeof window !== 'undefined' && window.sessionStorage) {
			const s = window.sessionStorage;
			return {
				get: (k) => {
					try {
						return s.getItem(k);
					} catch {
						return null;
					}
				},
				set: (k, v) => {
					try {
						s.setItem(k, v);
						return true;
					} catch {
						return false;
					}
				},
				remove: (k) => {
					try {
						s.removeItem(k);
					} catch {
						// Nothing.
					}
				},
				keys: () => [],
			};
		}
	} catch {
		// Fall through.
	}
	return fallbackSession;
}
const fallbackSession = memoryStore();

function isContext(v: unknown): v is ChallengeContext {
	if (typeof v !== 'object' || v === null) return false;
	const c = v as Record<string, unknown>;
	return (
		typeof c.code === 'string' &&
		typeof c.number === 'number' &&
		isLevel(c.level) &&
		typeof c.date === 'string' &&
		typeof c.durationMs === 'number' &&
		Array.isArray(c.paceMs) &&
		c.paceMs.every((x) => typeof x === 'number')
	);
}

/** Notes that the game in `slot` is played from this challenge (for this tab). */
export function setPlayingChallenge(slot: string, c: ChallengeContext, store: KeyValueStore = sessionStore()): void {
	store.set(challengeKeys.playing(slot), JSON.stringify({ v: 1, c }));
}

/** The challenge the game in `slot` is played from, if any. */
export function playingChallenge(slot: string, store: KeyValueStore = sessionStore()): ChallengeContext | null {
	try {
		const parsed = JSON.parse(store.get(challengeKeys.playing(slot)) ?? 'null') as { v?: unknown; c?: unknown } | null;
		return parsed?.v === 1 && isContext(parsed.c) ? parsed.c : null;
	} catch {
		return null;
	}
}

// --- Creating one --------------------------------------------------------------------------

/** Waits between tries while the solve reaches the api (404/409 until it has). */
export const CREATE_RETRY_MS = [400, 1200, 2500] as const;

export interface CreateDeps {
	create: typeof createChallenge;
	/** Push this daily's finished solve to the api (even with "Count my plays" off). */
	retrySolves: (daily: { number: number; level: Level }) => Promise<void>;
	deviceId: () => string;
	wait: (ms: number) => Promise<void>;
	track: typeof track;
	store: KeyValueStore;
}

const defaultDeps: CreateDeps = {
	create: createChallenge,
	retrySolves: pushSolveFor,
	deviceId: () => getDeviceId(platformStore),
	wait: (ms) => new Promise((r) => setTimeout(r, ms)),
	track,
	store: platformStore,
};

/**
 * This device's challenge code for a finished daily, from the api (creating it is
 * idempotent). The solve may still be on its way there (just finished, or offline a moment
 * ago), so a "no solve yet" answer pushes the pending solves and tries again a few times.
 * Remembers and tracks nothing: that's `recordMyChallenge`, once the dare is shared. Never
 * throws: null when it couldn't.
 */
async function challengeCode(input: { number: number; level: Level }, deps: CreateDeps): Promise<string | null> {
	try {
		const deviceId = deps.deviceId();
		if (!deviceId) return null;
		let result: CreateChallengeResult = await deps.create({ deviceId, number: input.number, level: input.level });
		for (const ms of CREATE_RETRY_MS) {
			if (result.ok || ![0, 404, 409].includes(result.status)) break;
			await deps.retrySolves({ number: input.number, level: input.level });
			await deps.wait(ms);
			result = await deps.create({ deviceId, number: input.number, level: input.level });
		}
		return result.ok ? result.code : null;
	} catch {
		return null;
	}
}

/**
 * Notes a challenge as shared today: remembered for Today's feedback line, and tracked as
 * `challenge_created` the first time this device shares it.
 */
export function recordMyChallenge(
	c: { code: string; number: number; level: Level; durationMs: number; todayKey: DateKey },
	deps: Pick<CreateDeps, 'store' | 'track'> = defaultDeps,
): void {
	try {
		const known = myChallenges(deps.store).some((m) => m.code === c.code);
		rememberChallenge({ code: c.code, createdOn: c.todayKey, number: c.number, level: c.level, durationMs: c.durationMs }, deps.store);
		if (!known) deps.track('challenge_created', { code: c.code, number: c.number, level: c.level });
	} catch {
		// A full or blocked store: the share still goes.
	}
}

/**
 * Creates (or fetches) this device's challenge for a finished daily and records it as
 * shared. Never throws: null when it couldn't.
 */
export async function createMyChallenge(
	input: { number: number; level: Level; durationMs: number; todayKey: DateKey },
	deps: CreateDeps = defaultDeps,
): Promise<string | null> {
	const code = await challengeCode(input, deps);
	if (code) recordMyChallenge({ ...input, code }, deps);
	return code;
}

const prepared = new Map<string, Promise<string | null>>();

/**
 * The code for a daily's challenge, asked for ahead of the tap (UX-045): the poster asks as
 * it opens, so "Challenge a friend" only has to format and share, inside the tap's user
 * gesture. Memoised per daily for the page; a failed try is forgotten, so the next asks
 * again. Records nothing (see `recordMyChallenge`).
 */
export function prepareMyChallenge(input: { number: number; level: Level }, deps: CreateDeps = defaultDeps): Promise<string | null> {
	const key = `${input.number}:${input.level}`;
	let hit = prepared.get(key);
	if (!hit) {
		hit = challengeCode(input, deps).then((code) => {
			if (!code) prepared.delete(key);
			return code;
		});
		prepared.set(key, hit);
	}
	return hit;
}

/** Tests: forget prepared codes. */
export function resetPreparedChallenges(): void {
	prepared.clear();
}
